import React, { useState, useEffect } from 'react';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import SplashScreen from './pages/SplashScreen';
import Onboarding from './pages/Onboarding';
import Landing from './pages/Landing';
import ResetPassword from './pages/ResetPassword';
import ComingSoon from './pages/ComingSoon';
import { readRecoveryFromUrl, clearRecoveryFromUrl } from './services/passwordReset';
import { isOnboardingDone, getOnboarding, saveOnboarding, loadRemoteOnboarding, pushRemoteOnboarding, clearOnboarding } from './services/onboardingStore';
import API, { SESSION_EXPIRED_EVENT } from './services/api';

const APP_HOST = 'app.nowatt.app';
const RESET_OK = 'Mot de passe mis à jour. Vous pouvez vous connecter avec le nouveau.';
const ACCOUNT_DELETED = 'Votre compte et vos données ont bien été supprimés.';
const SESSION_EXPIRED = 'Votre session a expiré. Reconnectez-vous pour continuer.';
const RESET_EXPIRED = 'Ce lien a expiré ou a déjà servi. Demandez un nouveau lien avec « Mot de passe oublié ? ».';
// L'API n'accepte les connexions que depuis le domaine officiel : si le lien de l'email
// a ouvert l'app ailleurs (nowatt.app, adresse Vercel…), on y renvoie l'utilisateur.
// Avant le lancement, seule la page d'attente est publique. La vraie app reste joignable
// par une porte discrète : ouvrir une fois https://app.nowatt.app/?acces sur un appareil,
// qui s'en souvient ensuite. Le jour du lancement, passer PRE_LAUNCH à false.
// Le serveur (vercel.json) renvoie app.nowatt.app vers la page d'attente pour tout visiteur
// sans le cookie « nowatt_acces » : le cookie est posé ici, en même temps que la mémoire locale.
const PRE_LAUNCH = true;
const ACCESS_KEY = 'nowatt_acces';
function rememberAccessCookie() {
  try {
    document.cookie = `${ACCESS_KEY}=1; Max-Age=31536000; Path=/; Secure; SameSite=Lax`;
  } catch { /* cookies bloqués : la porte reste ouverte via ?acces */ }
}
function hasPreviewAccess() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('acces')) {
      localStorage.setItem(ACCESS_KEY, '1');
      rememberAccessCookie();
      params.delete('acces');
      const q = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (q ? `?${q}` : '') + window.location.hash);
      return true;
    }
    const ok = localStorage.getItem(ACCESS_KEY) === '1';
    if (ok) rememberAccessCookie(); // appareils déjà passés par la porte avant ce changement
    return ok;
  } catch {
    return false;
  }
}

const onOfficialHost = () => [APP_HOST, 'localhost', '127.0.0.1'].includes(window.location.hostname);

function App() {
  const [previewAccess] = useState(() => !PRE_LAUNCH || hasPreviewAccess());
  const [showSplash, setShowSplash] = useState(true);
  const [user, setUser] = useState(null);
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [profileReady, setProfileReady] = useState(true);
  // Lien « mot de passe oublié » reçu par email : lu une seule fois au chargement
  const [recovery, setRecovery] = useState(() => readRecoveryFromUrl());
  const [loginNotice, setLoginNotice] = useState(() => {
    // Message transmis après une redirection vers le domaine officiel de l'app
    const flag = new URLSearchParams(window.location.search).get('mdp');
    if (!flag) return '';
    window.history.replaceState(null, '', window.location.pathname);
    return flag === 'ok' ? RESET_OK : RESET_EXPIRED;
  });

  // Detect if we're on the landing page domain
  const isLanding = window.location.hostname === 'nowatt.app' || window.location.hostname === 'www.nowatt.app';

  useEffect(() => {
    const token = localStorage.getItem('nowatt_token');
    const userId = localStorage.getItem('nowatt_user');
    if (token && userId) {
      const u = { access_token: token, user_id: userId };
      setUser(u);
      syncProfile(u);
    }
    // L'onboarding est suivi compte par compte, pas par navigateur
    setOnboardingDone(isOnboardingDone(userId));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Session devenue invalide pendant l'utilisation (renouvellement impossible) : retour à la connexion
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setOnboardingDone(false);
      setLoginNotice(SESSION_EXPIRED);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  // Récupère dans le compte les réponses de l'onboarding, pour qu'un utilisateur déjà passé
  // par là sur un autre appareil n'ait rien à ressaisir.
  const syncProfile = async (u) => {
    if (!u?.user_id || !u?.access_token) return;
    // Rien en local : on attend la réponse du compte avant de choisir l'écran
    if (!isOnboardingDone(u.user_id)) setProfileReady(false);
    let token = u.access_token;
    let res = await loadRemoteOnboarding(token);
    if (res.status === 'unauthorized') {
      // Jeton expiré : on le renouvelle une fois, sinon retour à la connexion
      try {
        const r = await API.post('/auth/refresh', { refresh_token: localStorage.getItem('nowatt_refresh_token') });
        token = r.data.access_token;
        localStorage.setItem('nowatt_token', token);
        if (r.data.refresh_token) localStorage.setItem('nowatt_refresh_token', r.data.refresh_token);
        setUser({ ...u, access_token: token });
        res = await loadRemoteOnboarding(token);
      } catch {
        handleLogout();
        setLoginNotice(SESSION_EXPIRED);
        setProfileReady(true);
        return;
      }
    }
    if (res.status === 'ok') {
      if (res.data) {
        saveOnboarding(u.user_id, res.data);
        setOnboardingDone(true);
      } else if (isOnboardingDone(u.user_id)) {
        // Réponses présentes seulement sur cet appareil : on les enregistre dans le compte
        pushRemoteOnboarding(token, getOnboarding(u.user_id));
      }
    }
    setProfileReady(true);
  };

  const handleLogin = (userData) => {
    setLoginNotice('');
    setUser(userData);
    setOnboardingDone(isOnboardingDone(userData?.user_id));
    syncProfile(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('nowatt_token');
    localStorage.removeItem('nowatt_refresh_token');
    localStorage.removeItem('nowatt_user');
    setUser(null);
    setOnboardingDone(false);
  };

  // Compte supprimé par l'utilisateur : on efface aussi ce que cet appareil en gardait
  const handleAccountDeleted = () => {
    clearOnboarding(user?.user_id);
    handleLogout();
    setLoginNotice(ACCOUNT_DELETED);
  };

  const handleOnboardingDone = () => {
    saveOnboarding(user?.user_id);
    setOnboardingDone(true);
  };

  // Fin du parcours de réinitialisation : retour à l'écran de connexion avec un message
  const finishRecovery = (ok) => {
    clearRecoveryFromUrl();
    if (!onOfficialHost()) { window.location.replace(`https://${APP_HOST}/?mdp=${ok ? 'ok' : 'expire'}`); return; }
    const notice = ok ? RESET_OK : RESET_EXPIRED;
    localStorage.removeItem('nowatt_token');
    localStorage.removeItem('nowatt_refresh_token');
    localStorage.removeItem('nowatt_user');
    setUser(null);
    setLoginNotice(notice);
    setRecovery(null);
    setShowSplash(false);
  };

  // Avant le lancement : tout le monde voit la page d'attente, sauf les appareils passés par la porte
  if (!previewAccess) {
    return <ComingSoon />;
  }

  // Lien de réinitialisation : prioritaire, quel que soit le domaine d'arrivée
  if (recovery?.accessToken) {
    return <ResetPassword accessToken={recovery.accessToken} onDone={finishRecovery} />;
  }
  if (recovery?.error) {
    clearRecoveryFromUrl();
    if (!onOfficialHost()) { window.location.replace(`https://${APP_HOST}/?mdp=expire`); return null; }
    return <Login onLogin={(u) => { setRecovery(null); handleLogin(u); }} notice={RESET_EXPIRED} />;
  }

  // Landing page for nowatt.app
  if (isLanding && !loginNotice) {
    return <Landing />;
  }

  // Splash screen for app.nowatt.app
  if (showSplash) {
    return <SplashScreen onDone={() => setShowSplash(false)} />;
  }

  // Le temps de lire les réponses enregistrées dans le compte
  if (user && !profileReady) {
    return (
      <div style={{ minHeight: '100vh', background: '#06080C', color: 'rgba(238,242,255,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '0.9rem' }}>
        Chargement de votre espace…
      </div>
    );
  }

  // App routing
  return (
    <div>
      {user ? (
        onboardingDone ? (
          <Dashboard user={user} onLogout={handleLogout} onAccountDeleted={handleAccountDeleted} onRestartOnboarding={() => setOnboardingDone(false)} />
        ) : (
          <Onboarding user={user} onDone={handleOnboardingDone} onLogout={handleLogout} />
        )
      ) : (
        <Login onLogin={handleLogin} notice={loginNotice} />
      )}
    </div>
  );
}

export default App;
